package com.cs.ejobin.ui.components

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.FilterChip
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.cs.ejobin.i18n.Keys
import com.cs.ejobin.i18n.t
import com.cs.ejobin.model.SkillCatalog
import com.cs.ejobin.ui.theme.AppColors

@Composable
fun SkillCatalogChips(
    title: String,
    all: List<SkillCatalog>,
    selected: Set<SkillCatalog>,
    onToggle: (SkillCatalog) -> Unit,
    enabled: Boolean,
    columns: Int = 3,
    collapsedCount: Int = 9,
    onClear: (() -> Unit)? = null
) {
    var expanded by remember { mutableStateOf(false) }

    val canExpand = all.size > collapsedCount
    val visible = if (expanded) all else all.take(collapsedCount)

    androidx.compose.foundation.layout.Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(10.dp)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                title,
                style = MaterialTheme.typography.titleSmall,
                modifier = Modifier.weight(1f),
                color = AppColors.TextPrimary
            )

            if (onClear != null && selected.isNotEmpty() && enabled) {
                TextButton(
                    onClick = onClear,
                    colors = ButtonDefaults.textButtonColors(contentColor = AppColors.Primary)
                ) { Text(t(Keys.clear_filters)) }
            }
        }

        visible.chunked(columns).forEach { row ->
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                row.forEach { skill ->
                    val on = selected.contains(skill)
                    FilterChip(
                        selected = on,
                        enabled = enabled,
                        onClick = { onToggle(skill) },
                        label = { Text(t(skill.labelKey)) },
                        modifier = Modifier.weight(1f)
                    )
                }
                repeat((columns - row.size).coerceAtLeast(0)) { Spacer(Modifier.weight(1f)) }
            }
        }

        if (canExpand) {
            TextButton(
                onClick = { expanded = !expanded },
                enabled = enabled,
                colors = ButtonDefaults.textButtonColors(contentColor = AppColors.Primary),
                modifier = Modifier.padding(top = 2.dp)
            ) {
                Text(if (expanded) t(Keys.show_less) else t(Keys.show_more))
            }
        }
    }
}

@Composable
fun ReadOnlySkillLabelChips(
    title: String,
    labels: List<String>,
    columns: Int = 3,
    collapsedCount: Int = 9
) {
    var expanded by remember { mutableStateOf(false) }

    val clean = remember(labels) { labels.map { it.trim() }.filter { it.isNotBlank() }.distinct() }
    val canExpand = clean.size > collapsedCount
    val visible = if (expanded) clean else clean.take(collapsedCount)

    androidx.compose.foundation.layout.Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(10.dp)
    ) {
        Text(title, style = MaterialTheme.typography.titleSmall, color = AppColors.TextPrimary)

        visible.chunked(columns).forEach { row ->
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                row.forEach { label ->
                    FilterChip(
                        selected = false,
                        enabled = false,
                        onClick = {},
                        label = { Text(label) },
                        modifier = Modifier.weight(1f)
                    )
                }
                repeat((columns - row.size).coerceAtLeast(0)) { Spacer(Modifier.weight(1f)) }
            }
        }

        if (canExpand) {
            TextButton(
                onClick = { expanded = !expanded },
                colors = ButtonDefaults.textButtonColors(contentColor = AppColors.Primary)
            ) {
                Text(if (expanded) t(Keys.show_less) else t(Keys.show_more))
            }
        }
    }
}